import {DynamicFormsExpression} from "../expressions/DynamicFormsExpression";
import {IfExpression} from "../expressions/IfExpression";
import {LiteralExpression} from "../expressions/LiteralExpression";
import {ReferenceExpression} from "../expressions/ReferenceExpression";
import {TodayExpression} from "../expressions/TodayExpression";
import {NotExpression} from "../expressions/NotExpression";
import {ComposedForm} from "../definitions/ComposedForm";
import {ComposedQuestion} from "../definitions/ComposedQuestion";
import {QuestionValidator} from "../definitions/QuestionValidator";
import {FormLayout} from "../definitions/FormLayout";
import {LayoutStep} from "../definitions/LayoutStep";
import {LayoutElement} from "../definitions/LayoutElement";
import {LayoutGenericElement} from "../definitions/LayoutGenericElement";
import {LayoutQuestionElement} from "../definitions/LayoutQuestionElement";
import {LayoutLabelElement} from "../definitions/LayoutLabelElement";
import {LayoutGroupElement} from "../definitions/LayoutGroupElement";
import {StringJoinExpression} from "../expressions/StringJoinExpression";
import {AllExpression} from "../expressions/AllExpression";
import {GreaterThanOrEqualToExpression} from "../expressions/GreaterThanOrEqualToExpression";
import {AnyExpression} from "../expressions/AnyExpression";
import {EqualToExpression} from "../expressions/EqualToExpression";

// noinspection JSUnusedLocalSymbols
export class FormVisitor {
	walkForm(form: ComposedForm) {
		form.questions.forEach(it => this.walkQuestion(it));
		this.walkLayout(form.layout);
		
		this.visitForm(form);
		return this;
	}
	
	walkQuestion(question: ComposedQuestion) {
		this.walkExpression(question.labelExpression);
		this.walkExpression(question.valueMapping);
		this.walkExpression(question.defaultValueExpression);
		this.walkExpression(question.visibleExpression);
		this.walkExpression(question.enabledExpression);
		this.walkExpression(question.editableExpression);
		this.walkExpression(question.requiredExpression);
		question.validators.forEach(it => this.walkValidator(it));
		
		this.visitQuestion(question);
		return this;
	}
	
	walkValidator(validator: QuestionValidator) {
		// todo walk validator types
		
		this.visitValidator(validator);
		return this;
	}
	
	walkLayout(layout: FormLayout) {
		layout.steps.forEach(it => this.walkLayoutStep(it));
		
		this.visitLayout(layout);
		return this;
	}
	
	walkLayoutStep(step: LayoutStep) {
		step.elements.forEach(it => this.walkLayoutElement(it));
		
		this.visitLayoutStep(step);
		return this;
	}
	
	walkLayoutElement(element: LayoutElement) {
		switch (element.type) {
			case "group":
				this.walkLayoutGroupElement(element as LayoutGroupElement);
				break;
			case "label":
				this.walkLayoutLabelElement(element as LayoutLabelElement);
				break;
			case "question":
				this.walkLayoutQuestionElement(element as LayoutQuestionElement);
				break;
			case "generic":
				this.walkLayoutGenericElement(element as LayoutGenericElement);
				break;
			default:
				console.warn(`Unknown element of type '${element.type}'.`);
				break;
		}
		
		this.visitLayoutElement(element);
		return this;
	}
	
	private walkLayoutGroupElement(element: LayoutGroupElement) {
		this.walkExpression(element.visibleExpression);
		element.elements.forEach(it => this.walkLayoutElement(it));
		
		this.visitGroupElement(element);
		return this;
	}
	
	private walkLayoutLabelElement(element: LayoutLabelElement) {
		this.walkExpression(element.visibleExpression);
		this.walkExpression(element.textExpression);
		
		this.visitLabelElement(element);
		return this;
	}
	
	private walkLayoutQuestionElement(element: LayoutQuestionElement) {
		this.walkExpression(element.visibleExpression);
		
		this.visitQuestionElement(element);
		return this;
	}
	
	private walkLayoutGenericElement(element: LayoutGenericElement) {
		this.walkExpression(element.visibleExpression);
		
		this.visitGenericElement(element);
		return this;
	}
	
	walkExpression(expression: DynamicFormsExpression | null) {
		if (!expression)
			return;
		
		switch (expression.function) {
			case "literal":
				this.walkLiteralExpression(expression as LiteralExpression);
				break;
			case "reference":
				this.walkReferenceExpression(expression as ReferenceExpression);
				break;
			case "all":
				this.walkAllExpression(expression as AllExpression);
				break;
			case "not":
				this.walkNotExpression(expression as NotExpression);
				break;
			case "if":
				this.walkIfExpression(expression as IfExpression);
				break;
			case "today":
				this.walkTodayExpression(expression as TodayExpression);
				break;
			case "stringJoin":
				this.walkStringJoinExpression(expression as StringJoinExpression);
				break;
			case "greaterThanOrEqualTo":
				this.walkGreaterThanOrEqualToExpression(expression as GreaterThanOrEqualToExpression);
				break;
			case "equalTo":
				this.walkEqualToExpression(expression as EqualToExpression);
				break;
			case "any":
				this.walkAnyExpression(expression as AnyExpression);
				break;
			default:
				console.warn(`Unknown expression of function '${expression.function}'.`);
				break;
		}
		
		this.visitExpression(expression);
	}
	
	private walkLiteralExpression(expression: LiteralExpression) {
		this.visitLiteralExpression(expression);
	}
	
	private walkReferenceExpression(expression: ReferenceExpression) {
		this.visitReferenceExpression(expression);
	}
	
	private walkAllExpression(expression: AllExpression) {
		expression.expr.forEach(it => this.walkExpression(it));
		
		this.visitAllExpression(expression);
	}
	
	private walkNotExpression(expression: NotExpression) {
		this.walkExpression(expression.expr);
		
		this.visitNotExpression(expression);
	}
	
	private walkStringJoinExpression(expression: StringJoinExpression) {
		expression.values.forEach(it => this.walkExpression(it));
		this.walkExpression(expression.delimiter);
		
		this.visitStringJoinExpression(expression);
	}
	
	private walkGreaterThanOrEqualToExpression(expression: GreaterThanOrEqualToExpression) {
		this.walkExpression(expression.left);
		this.walkExpression(expression.right);
		
		this.visitGreaterThanOrEqualToExpression(expression);
	}
	
	private walkEqualToExpression(expression: EqualToExpression) {
		this.walkExpression(expression.left);
		this.walkExpression(expression.right);
		
		this.visitEqualToExpression(expression);
	}
	
	private walkAnyExpression(expression: AnyExpression) {
		expression.expr.forEach(it => this.walkExpression(it));
		
		this.visitAnyExpression(expression);
	}
	
	private walkIfExpression(expression: IfExpression) {
		this.walkExpression(expression.condition);
		this.walkExpression(expression.then);
		this.walkExpression(expression.else);
		
		this.visitIfExpression(expression);
	}
	
	protected visitForm(form: ComposedForm) {
		// empty placeholder method
	}
	
	protected visitQuestion(question: ComposedQuestion) {
		// empty placeholder method
	}
	
	protected visitValidator(validator: QuestionValidator) {
		// empty placeholder method
	}
	
	protected visitLayout(layout: FormLayout) {
		// empty placeholder method
	}
	
	protected visitLayoutStep(step: LayoutStep) {
		// empty placeholder method
	}
	
	protected visitLayoutElement(element: LayoutElement) {
		// empty placeholder method
	}
	
	protected visitGroupElement(element: LayoutGroupElement) {
		// empty placeholder method
	}
	
	protected visitLabelElement(element: LayoutLabelElement) {
		// empty placeholder method
	}
	
	protected visitQuestionElement(element: LayoutQuestionElement) {
		// empty placeholder method
	}
	
	protected visitGenericElement(element: LayoutGenericElement) {
		// empty placeholder method
	}
	
	private walkTodayExpression(expression: TodayExpression) {
		this.visitTodayExpression(expression);
	}
	
	protected visitExpression(expression: DynamicFormsExpression) {
		// empty placeholder method
	}
	
	protected visitLiteralExpression(expression: LiteralExpression) {
		// empty placeholder method
	}
	
	protected visitReferenceExpression(expression: ReferenceExpression) {
		// empty placeholder method
	}
	
	protected visitAllExpression(expression: AllExpression) {
		// empty placeholder method
	}
	
	protected visitNotExpression(expression: NotExpression) {
		// empty placeholder method
	}
	
	protected visitStringJoinExpression(expression: StringJoinExpression) {
		// empty placeholder method
	}
	
	protected visitGreaterThanOrEqualToExpression(expression: GreaterThanOrEqualToExpression) {
		// empty placeholder method
	}
	
	protected visitEqualToExpression(expression: EqualToExpression) {
		// empty placeholder method
	}
	
	protected visitAnyExpression(expression: AnyExpression) {
		// empty placeholder method
	}
	
	protected visitIfExpression(expression: IfExpression) {
		// empty placeholder method
	}
	
	protected visitTodayExpression(expression: TodayExpression) {
		// empty placeholder method
	}
}
